import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api, apiBlob } from '../lib/api.ts';
import { Boton, Insignia, Tarjeta } from './ui.tsx';

interface ImportJob {
  id: string;
  tipo: string;
  modo: string;
  estado: string;
  resumen: {
    totalFilas: number;
    creados: number;
    actualizados: number;
    errores: number;
    sinImagen: string[];
    marcasNuevas?: string[];
    credencialesGeneradas?: number;
  } | null;
  errores: { fila: number; referencia?: string; motivo: string }[] | null;
  createdAt: string;
}

/** Historial de importaciones (los popups muestran el resultado al momento; esto es el archivo). */
export function HistorialImportaciones() {
  const qc = useQueryClient();
  const jobs = useQuery({
    queryKey: ['admin-importaciones'],
    queryFn: () => api<{ items: ImportJob[] }>('/admin/importaciones'),
  });

  async function descargarCredenciales(id: string) {
    const blob = await apiBlob(`/admin/importaciones/${id}/credenciales.xlsx`);
    if (!blob) return;
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `credenciales-${id}.xlsx`;
    a.click();
    URL.revokeObjectURL(url);
    await qc.invalidateQueries({ queryKey: ['admin-importaciones'] });
  }

  return (
    <Tarjeta className="p-5">
      <h2 className="text-[17px] font-medium">Historial de importaciones</h2>
      <ul className="mt-3 space-y-2">
        {jobs.data?.items.map((j) => (
          <li key={j.id} className="interno p-3 text-sm">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <p className="flex items-center gap-2 font-medium">
                {j.tipo}
                <Insignia>{j.modo === 'DRY_RUN' ? 'análisis' : 'aplicado'}</Insignia>
                <Insignia tono={j.estado === 'LISTO' ? 'ok' : j.estado === 'ERROR' ? 'alerta' : 'gris'}>
                  {j.estado}
                </Insignia>
                <span className="text-xs font-normal text-suave">
                  {new Date(j.createdAt).toLocaleString()}
                </span>
              </p>
              {j.tipo === 'CLIENTES' && j.modo === 'APPLY' && j.estado === 'LISTO' && (j.resumen?.credencialesGeneradas ?? 0) > 0 ? (
                <Boton variante="contorno" tamano="sm" onClick={() => void descargarCredenciales(j.id)}>
                  Credenciales.xlsx
                </Boton>
              ) : null}
            </div>
            {j.resumen ? (
              <p className="mt-1 text-suave">
                {j.resumen.totalFilas} filas · {j.resumen.creados} nuevos · {j.resumen.actualizados} actualizados ·{' '}
                {j.resumen.errores} errores
                {j.resumen.sinImagen.length > 0 ? ` · ${j.resumen.sinImagen.length} sin foto` : ''}
                {(j.resumen.marcasNuevas?.length ?? 0) > 0 ? ` · marcas creadas: ${j.resumen.marcasNuevas!.join(', ')}` : ''}
              </p>
            ) : null}
            {j.errores && j.errores.length > 0 ? (
              <ul className="mt-2 max-h-32 space-y-1 overflow-y-auto rounded-2xl bg-rose-500/10 p-3 text-xs text-rose-500">
                {j.errores.map((e, i) => (
                  <li key={i}>
                    Fila {e.fila} {e.referencia ? `(${e.referencia})` : ''}: {e.motivo}
                  </li>
                ))}
              </ul>
            ) : null}
          </li>
        ))}
        {jobs.data?.items.length === 0 ? (
          <li className="py-3 text-sm text-suave">Todavía no hay importaciones.</li>
        ) : null}
      </ul>
    </Tarjeta>
  );
}
